import { Api, LogManager, ProcessLock, ServerError } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { Button, DataSourceMode, Layout, SlideoutDecorator } from "@mcleod/components";
import { CompareOrder } from "./CompareOrder";
import { EdiBilling } from "./EdiBilling";

const log = LogManager.getLogger("lme.datafusion.CompareInvoice");
const LOCK_NAME = "DataFusion compare";

export class CompareInvoice {
    private _movementId: string = null;

    async compareInvoice(invoiceId: string, orderId: string, moveId: string, invoiceNumberString: string, ediBilling?: EdiBilling): Promise<any> {
        return new Promise(async (resolve, reject) => {
            if (invoiceId) {
                // Validate movement ID for locking
                if (!moveId) {
                    log.error("Movement ID is required to compare invoice.");
                    reject("Movement ID is required to compare invoice.");
                    return;
                }
                this._movementId = moveId;

                const movementLocked = await Api.search("lme/datafusion/check-movement-lock", { movement_id: this._movementId });
                if (movementLocked.data[0].is_locked) {
                    reject(movementLocked.data[0].details);
                    return;
                }

                // Acquire lock before proceeding with compare
                ProcessLock.acquireLock(LOCK_NAME, this._movementId).then(async lockResult => {
                    if (lockResult.success !== true) {
                        await CommonDialogs.showDialog(lockResult.message, { title: "Movement Locked" });
                        return;
                    }

                try {
                    const result = await Api.search("lme/datafusion/compare-invoice", { invoice_id: invoiceId });

                    // Check if we actually got data back
                    if (!result?.data || result.data.length === 0) {
                        const errorMsg = "No data returned from compare invoice API. The invoice may not have a valid EDI profile configured.";
                        log.error(errorMsg);
                        await CommonDialogs.showError(errorMsg, "Compare Invoice Error");
                        reject(new Error(errorMsg));
                        return;
                    }

                    // Check for explicit error messages in the response
                    if (result.data[0]?.error_message || result.data[0]?.message) {
                        const errorMsg = result.data[0].error_message || result.data[0].message;
                        log.error("API returned error:", errorMsg);
                        await CommonDialogs.showError(errorMsg, "Compare Invoice Error");
                        reject(new Error(errorMsg));
                        return;
                    }

                    if (result.data[0]?.no_changes_found === "Y") {
                        CommonDialogs.showMessage("No changes were found");
                        resolve(result);
                        return;
                    }

                    // Warn about missing cross-references before showing compare UI
                    if (result.data[0]?.missing_xref_warning) {
                        const warningMsg = result.data[0].missing_xref_warning
                            + "\n\nWould you still like to continue running the Compare?";
                        const continueCompare = await CommonDialogs.showYesNo(warningMsg, "Missing Cross References");
                        if (!continueCompare) {
                            await this.releaseLock();
                            resolve(result);
                            return;
                        }
                    }

                    // Only show messages if they have meaningful content
                    if (result.data[0]?.order_allowed && result.data[0].order_allowed !== "Allowed") {
                        await CommonDialogs.showMessage(result.data[0].order_allowed);
                        resolve(result);
                        return;
                    }
                    if (result.data[0]?.movement_allowed && result.data[0].movement_allowed !== "Allowed") {
                        await CommonDialogs.showMessage(result.data[0].movement_allowed);
                        resolve(result);
                        return;
                    }

                    const compareLayout = Layout.getLayout("lme/datafusion/CompareOrder") as CompareOrder;
                    compareLayout.isInvoice = true;
                    compareLayout.loadFromApiData(result.data[0], DataSourceMode.ADD);
                    compareLayout.invoiceId = invoiceId;
                    compareLayout.orderId = orderId;
                    compareLayout.moveId = moveId;

                    const btnApplyChanges = new Button({ caption: "Apply Changes", rowBreak: false, backgroundColor: "primary", color: "McLeodWhite" });
                    btnApplyChanges.addClickListener(async event => {
                        btnApplyChanges.busy = true;
                        compareLayout.applyChanges(btnApplyChanges);
                    });
                    compareLayout.buttonApply = btnApplyChanges;
                    await this.getCompareSlideOutDecorator(compareLayout, invoiceNumberString, btnApplyChanges, ediBilling);
                    resolve(result);
                } catch (error: unknown) {
                    log.error("Error in compareInvoice:", error);
                    // Enhance error message if it's not already descriptive
                    let errorMessage: string;
                    if (error instanceof ServerError) {
                        errorMessage = error.toString();
                    } else if (error instanceof Error) {
                        errorMessage = error.message;
                    } else if (typeof error === "string") {
                        errorMessage = error;
                    } else {
                        errorMessage = "Failed to compare invoice. Please check that the invoice has a valid EDI profile configured.";
                    }
                    await CommonDialogs.showError(errorMessage, "Compare Invoice Error");
                    await this.releaseLock();
                    reject(new Error(errorMessage));
                }
                }).catch((error) => {
                    CommonDialogs.showError(error, "Compare Invoice Lock");
                    reject(error);
                });
            }
        })
    }

    getCompareSlideOutDecorator(compareLayout: CompareOrder, invoiceId: string, btnApplyChanges: Button, ediBilling: EdiBilling): Promise<SlideoutDecorator> {
        return new Promise(async (resolve, reject) => {
            const compareSOD = new SlideoutDecorator({
                layout: compareLayout,
                title: "Compare Invoice - " + invoiceId,
                fillVerticalSpace: true,
                fillHorizontalSpace: false,
                width: window.innerWidth * 0.95,
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    compareLayout.insert(btnApplyChanges, 3);
                    compareLayout.buttonSelectAll.visible = compareLayout.isSelectAllVisible;
                    compareLayout.buttonDeselectAll.visible = compareLayout.isSelectAllVisible;
                    compareLayout.buttonApply.visible = compareLayout.isSelectAllVisible;
                },
                doAfterSlideOut: async (decorator: SlideoutDecorator) => {
                    try {
                        if (compareLayout.changesApplied > 0 && ediBilling) {
                            ediBilling.mainDataSource.search(ediBilling.mainDataSource.lastSearch);
                        }
                    } finally {
                        await this.releaseLock();
                    }
                    resolve(compareSOD);
                }
            });
        });
    }

    async releaseLock() {
        if (this._movementId) {
            await ProcessLock.releaseLock(LOCK_NAME, this._movementId);
        }
    }
}
